/**
 * Helpers to merge multiple full HTML print documents into one printable page set.
 * Each source document is assumed to be a complete `<!DOCTYPE html>…</html>` string
 * (as produced by existing `build*PrintHtml` builders).
 */

/**
 * @param {string} fullHtml
 * @returns {{ styles: string, body: string }}
 */
export function extractHtmlParts(fullHtml) {
  const html = String(fullHtml || "");
  const styles = [];
  const styleRe = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let match;
  while ((match = styleRe.exec(html)) !== null) {
    styles.push(match[1]);
  }
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const body = bodyMatch ? bodyMatch[1].trim() : html.trim();
  return { styles: styles.join("\n"), body };
}

/**
 * Combine multiple print HTML documents into a single document with page breaks.
 *
 * @param {Array<{ html: string, title?: string }>} documents
 * @param {{ documentTitle?: string }} [options]
 * @returns {string}
 */
export function composeMultiDocumentPrintHtml(documents, options = {}) {
  const list = Array.isArray(documents)
    ? documents.filter((d) => d?.html && String(d.html).trim())
    : [];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0].html;

  const parts = list.map((doc) => extractHtmlParts(doc.html));
  const allStyles = parts.map((p) => p.styles).filter(Boolean).join("\n");
  const sheets = parts
    .map((p, i) => {
      const breakClass = i > 0 ? " print-doc-break" : "";
      return `<section class="print-doc-sheet${breakClass}" data-print-index="${i + 1}">${p.body}</section>`;
    })
    .join("\n");

  const title = options.documentTitle || `HR Documents (${list.length})`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeAttr(title)}</title>
  <style>
    @page { margin: 12mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    .print-doc-sheet { background: #fff; }
    .print-doc-break { page-break-before: always; break-before: page; }
    ${allStyles}
  </style>
</head>
<body>
${sheets}
</body>
</html>`;
}

function escapeAttr(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
